import "./Features.css";
import starPng from "../../assets/sparkles.png";
import futurePng from "../../assets/plus-small.png";
import screen1 from "../../assets/1.png";
import screen2 from "../../assets/2.png";
import screen3 from "../../assets/3.png";
import screen4 from "../../assets/4.png";
import appStoreBadge from "../../assets/app-store-badge.svg";
import googlePlayBadge from "../../assets/google-play-badge.png";
import {Helmet} from "react-helmet";

const portalSections = [
  {
    title: "Account & Access",
    items: [
      "Partner registration and verification (KYC, billing details).",
      "User roles: admin, reseller, support.",
      "Multi-language interface and multi-currency.",
    ],
  },
  {
    title: "Ordering eSIMs & SIMs",
    items: [
      "Bulk ordering and batch provisioning.",
      "QR codes: generate, download, direct sharing.",
      "Activation via QR or Activation Code; device compatibility check.",
      "Physical SIMs: neutral or fully branded.",
    ],
  },
  {
    title: "Products & Plans",
    items: [
      "GB-based, Unlimited, Subscriptions, PAYG.",
      "Regional/global packages; IoT-optimised plans.",
      "Multi-currency pricing, promos/discounts/vouchers, auto‑renew.",
    ],
  },
  {
    title: "Billing & Payments",
    items: [
      "Per GB/MB/flat-rate models.",
      "Digital wallet, transfers, reports, invoicing.",
      "Wallet bonuses for payments.",
    ],
  },
  {
    title: "Monitoring & Reporting",
    items: [
      "Real-time dashboard, CSV/XLS exports.",
      "Cost analysis by country/region/user.",
      "Full history, low-balance/expiry notifications.",
    ],
  },
  {
    title: "Integration & API",
    items: ["Provisioning and Billing APIs.", "CRM/ERP/partner integrations."],
  },
  {
    title: "Support & Security",
    items: [
      "2FA, encryption, GDPR, SM‑DP+ compliance.",
      "24/7 Priority Escalation, dedicated CSM.",
    ],
  },
  {
    title: "Planned",
    items: [
      "Phone numbers (voice/SMS), in-app chat.",
      "In-app SMS and voice handling.",
      "Advanced white‑label tools.",
    ],
  },
];

const appFeatures = [
  "Instant eSIM Activation — purchase and connect in minutes.",
  "Flexible, Affordable Data Plans — from short-term passes to long-term bundles.",
  "Multiple eSIM Options — choose from data packages (GB-based), unlimited bundles, subscriptions, or true pay-as-you-go, where you only pay for what you use.",
  "All-In-One Management — buy, activate, and manage all your eSIMs in one app.",
  "Easy Setup — install via QR code or activation code.",
  "Direct QR Sharing — share eSIM QR codes instantly with family, friends, or colleagues.",
  "Check eSIM Compatibility — verify device readiness directly in the app.",
  "Smart Auto-Renew — packages can automatically renew when the current one is about to run out, so you never lose connectivity.",
  "Usage Notifications — receive alerts before your package expires or data is nearly consumed.",
  "Privacy & Security — GDPR-compliant, no ads, no tracking.",
  "Multi-Language Support — app interface available in multiple languages.",
  "Global Coverage — reliable data in 190+ countries.",
  "Currency Comparison — view plan costs in different currencies.",
  "Smart Wallet Benefits — enjoy a permanent 3% discount when paying with your wallet.",
  "Wallet Transfers — send balance to another user — ideal for families and businesses.",
  "Location-based Guidance — if you consent to share your location, the app identifies your country/region and suggests the best plan.",
];

const upcoming = [
  "Purchase of phone numbers — support for inbound and outbound voice calls and SMS.",
  "Built-in support chat — direct contact with customer service from inside the app.",
  "In-app SMS — send and receive text messages directly in the application.",
  "In-app voice calls — make and receive phone calls without leaving the app.",
];

// dzieli tylko po “ spacja — spacja ” (również en-dash/minus)
const splitAtDash = (s) => {
  const re = /\s(—|–|-)\s/;
  const m = s.match(re);
  if (!m) return { head: s, tail: "" };
  const idx = m.index;
  return {
    head: s.slice(0, idx).trimEnd(),
    tail: s.slice(idx + m.length).trimStart(),
  };
};

export default function Features() {
  const left = appFeatures.slice(0, 8);
  const right = appFeatures.slice(8);

  return (
    <main className="features">
        <Helmet>
            <title>GlobTel Esim - Features</title>
        </Helmet>
      <header className="features__hero">
        <h1>Features</h1>
        <p className="features__lead">
          Discover every feature of our platform: from the partner web portal to
          the mobile app, all the capabilities gathered here.
        </p>
      </header>

      {/* Portal feature cards */}
      <section className="features__portal" aria-label="Portal features">
        <h2>Globtel Wholesale eSIM Portal</h2>
        <div className="feat-grid">
          {portalSections.map((sec) => (
            <article key={sec.title}>
              <h3>{sec.title}</h3>
              <ul>
                {sec.items.map((it, i) => (
                  <li key={i}>{it}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* Mobile app features 2-col list */}
      <section className="pricing__app" aria-label="Mobile app">
        <h2>Mobile App</h2>
        <div className="app-grid">
          <ul className="app-col">
            {left.map((txt, i) => {
              const { head, tail } = splitAtDash(txt);
              return (
                <li key={i}>
                  <span className="li-icon" aria-hidden="true">
                    <img src={starPng} alt="" />
                  </span>
                  <span className="li-text">
                    {tail ? (
                      <>
                        <strong className="li-head">{head}</strong>
                        {" — "}
                        {tail}
                      </>
                    ) : (
                      txt
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
          <ul className="app-col">
            {right.map((txt, i) => {
              const { head, tail } = splitAtDash(txt);
              return (
                <li key={i}>
                  <span className="li-icon" aria-hidden="true">
                    <img src={starPng} alt="" />
                  </span>
                  <span className="li-text">
                    {tail ? (
                      <>
                        <strong className="li-head">{head}</strong>
                        {" — "}
                        {tail}
                      </>
                    ) : (
                      txt
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Upcoming */}
        <section className="upcoming" aria-label="Upcoming features">
          <div className="upcoming__inner">
            <h2>Upcoming features</h2>
            <ul className="upcoming__list">
              {upcoming.map((txt, i) => {
                const { head, tail } = splitAtDash(txt);
                return (
                  <li key={i}>
                    <span className="li-icon" aria-hidden="true">
                      <img src={futurePng} alt="" />
                    </span>
                    <span className="li-text">
                      {tail ? (
                        <>
                          <strong className="li-head">{head}</strong>
                          {" — "}
                          {tail}
                        </>
                      ) : (
                        txt
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Store badges */}
        <section className="store-cta" aria-label="Get the app">
          <h2 className="store-cta__title">Get the App</h2>
          <div className="store-badges">
            <a
              className="badge"
              href="https://play.google.com/store/apps/details?id=com.simgo&pcampaignid=web_share"
              aria-label="Get it on Google Play"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={googlePlayBadge}
                alt="Get it on Google Play"
                height="60"
              />
            </a>
            <a
              className="badge"
              href="https://apps.apple.com/de/app/simgo/id6751269737?l=en-GB"
              aria-label="Download on the App Store"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src={appStoreBadge}
                alt="Download on the App Store"
                height="60"
              />
            </a>
          </div>
        </section>

        {/* Screens */}
        <div className="screens">
          <img src={screen1} alt="App screen 1" loading="lazy" />
          <img src={screen2} alt="App screen 2" loading="lazy" />
          <img src={screen3} alt="App screen 3" loading="lazy" />
          <img src={screen4} alt="App screen 4" loading="lazy" />
        </div>
      </section>
    </main>
  );
}
